type SlotValue = { max?: number; used?: number; avail?: unknown };

/** 1 = slot available, 0 = spent. When `avail` is missing, derive from max/used assuming auto-use consumes from the right (RTL). */
export type SpellSlotEntry = { max: number; used: number; avail?: number[] };

export type SpellSlotMap = Record<string, SpellSlotEntry>;

function toInt(value: unknown, fallback = 0): number {
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function isAvailArray(raw: unknown, max: number): raw is number[] {
  if (!Array.isArray(raw) || raw.length !== max) return false;
  return raw.every((x) => x === 0 || x === 1 || x === true || x === false);
}

/** Per-dot availability: index 0 = left, max-1 = right. Auto-spend uses rightmost available first (RTL). */
export function getSlotAvailability(entry: { max?: number; used?: number; avail?: unknown } | undefined): number[] {
  const max = Math.max(0, toInt(entry?.max));
  if (max === 0) return [];
  const used = Math.max(0, Math.min(max, toInt(entry?.used)));
  const raw = entry?.avail;
  if (isAvailArray(raw, max)) {
    return raw.map((x) => (x === true || x === 1 ? 1 : 0));
  }
  return Array.from({ length: max }, (_, i) => (i < max - used ? 1 : 0));
}

export function availabilityToUsed(max: number, avail: number[]): number {
  return max - avail.reduce((sum, a) => sum + (a === 1 ? 1 : 0), 0);
}

export function normalizeSpellSlots(raw: Record<string, SlotValue> | null | undefined): SpellSlotMap {
  const entries = Object.entries(raw || {});
  const normalized: SpellSlotMap = {};
  entries.forEach(([key, value]) => {
    const max = Math.max(0, toInt(value?.max));
    if (max === 0) {
      normalized[key] = { max: 0, used: 0 };
      return;
    }
    const rawAvail = value?.avail;
    let avail: number[];
    if (isAvailArray(rawAvail, max)) {
      avail = rawAvail.map((x) => (x === true || x === 1 ? 1 : 0));
    } else {
      const used = Math.max(0, Math.min(max, toInt(value?.used)));
      avail = Array.from({ length: max }, (_, i) => (i < max - used ? 1 : 0));
    }
    const used = availabilityToUsed(max, avail);
    normalized[key] = { max, used, avail };
  });
  return normalized;
}

export function remainingSlots(slots: SpellSlotMap, level: number): number {
  const key = `level_${level}`;
  const slot = slots[key];
  if (!slot) return 0;
  return getSlotAvailability(slot).filter((a) => a === 1).length;
}

export function canCastAtLevel(spellLevel: number, castLevel: number, slots: SpellSlotMap): boolean {
  if (spellLevel <= 0) return true;
  if (castLevel < spellLevel) return false;
  return remainingSlots(slots, castLevel) > 0;
}

/** Lowest slot level that can cast this spell (supports upcasting). Returns -1 if none. */
export function findAutoCastLevel(spellLevel: number, slots: SpellSlotMap): number {
  if (spellLevel <= 0) return 0;
  for (let castLevel = Math.max(1, spellLevel); castLevel <= 9; castLevel++) {
    if (remainingSlots(slots, castLevel) > 0) return castLevel;
  }
  return -1;
}

export function canAutoCastSpell(spellLevel: number, slots: SpellSlotMap): boolean {
  const castLevel = findAutoCastLevel(spellLevel, slots);
  return castLevel >= 0 && canCastAtLevel(spellLevel, castLevel, slots);
}

export function classProgression(className: string | null | undefined): 'none' | 'full' | 'half' | 'third' | 'pact' {
  const c = String(className || '').trim().toLowerCase();
  if (c === 'warlock') return 'pact';
  if (c === 'paladin' || c === 'ranger' || c === 'artificer') return 'half';
  if (c === 'fighter' || c === 'rogue') return 'third';
  if (['bard', 'cleric', 'druid', 'sorcerer', 'wizard'].includes(c)) return 'full';
  return 'none';
}

const FULL = {
  1: [2, 0, 0, 0, 0, 0, 0, 0, 0], 2: [3, 0, 0, 0, 0, 0, 0, 0, 0], 3: [4, 2, 0, 0, 0, 0, 0, 0, 0],
  4: [4, 3, 0, 0, 0, 0, 0, 0, 0], 5: [4, 3, 2, 0, 0, 0, 0, 0, 0], 6: [4, 3, 3, 0, 0, 0, 0, 0, 0],
  7: [4, 3, 3, 1, 0, 0, 0, 0, 0], 8: [4, 3, 3, 2, 0, 0, 0, 0, 0], 9: [4, 3, 3, 3, 1, 0, 0, 0, 0],
  10: [4, 3, 3, 3, 2, 0, 0, 0, 0], 11: [4, 3, 3, 3, 2, 1, 0, 0, 0], 12: [4, 3, 3, 3, 2, 1, 0, 0, 0],
  13: [4, 3, 3, 3, 2, 1, 1, 0, 0], 14: [4, 3, 3, 3, 2, 1, 1, 0, 0], 15: [4, 3, 3, 3, 2, 1, 1, 1, 0],
  16: [4, 3, 3, 3, 2, 1, 1, 1, 0], 17: [4, 3, 3, 3, 2, 1, 1, 1, 1], 18: [4, 3, 3, 3, 3, 1, 1, 1, 1],
  19: [4, 3, 3, 3, 3, 2, 1, 1, 1], 20: [4, 3, 3, 3, 3, 2, 2, 1, 1],
} as const;

const HALF = {
  1: [0, 0, 0, 0, 0], 2: [2, 0, 0, 0, 0], 3: [3, 0, 0, 0, 0], 4: [3, 0, 0, 0, 0], 5: [4, 2, 0, 0, 0],
  6: [4, 2, 0, 0, 0], 7: [4, 3, 0, 0, 0], 8: [4, 3, 0, 0, 0], 9: [4, 3, 2, 0, 0], 10: [4, 3, 2, 0, 0],
  11: [4, 3, 3, 0, 0], 12: [4, 3, 3, 0, 0], 13: [4, 3, 3, 1, 0], 14: [4, 3, 3, 1, 0], 15: [4, 3, 3, 2, 0],
  16: [4, 3, 3, 2, 0], 17: [4, 3, 3, 3, 1], 18: [4, 3, 3, 3, 1], 19: [4, 3, 3, 3, 2], 20: [4, 3, 3, 3, 2],
} as const;

const THIRD = {
  1: [0, 0, 0, 0], 2: [0, 0, 0, 0], 3: [2, 0, 0, 0], 4: [3, 0, 0, 0], 5: [3, 0, 0, 0], 6: [3, 0, 0, 0],
  7: [4, 2, 0, 0], 8: [4, 2, 0, 0], 9: [4, 2, 0, 0], 10: [4, 3, 0, 0], 11: [4, 3, 0, 0], 12: [4, 3, 0, 0],
  13: [4, 3, 2, 0], 14: [4, 3, 2, 0], 15: [4, 3, 2, 0], 16: [4, 3, 3, 0], 17: [4, 3, 3, 0], 18: [4, 3, 3, 0],
  19: [4, 3, 3, 1], 20: [4, 3, 3, 1],
} as const;

const PACT = {
  1: { slots: 1, level: 1 }, 2: { slots: 2, level: 1 }, 3: { slots: 2, level: 2 }, 4: { slots: 2, level: 2 },
  5: { slots: 2, level: 3 }, 6: { slots: 2, level: 3 }, 7: { slots: 2, level: 4 }, 8: { slots: 2, level: 4 },
  9: { slots: 2, level: 5 }, 10: { slots: 2, level: 5 }, 11: { slots: 3, level: 5 }, 12: { slots: 3, level: 5 },
  13: { slots: 3, level: 5 }, 14: { slots: 3, level: 5 }, 15: { slots: 3, level: 5 }, 16: { slots: 3, level: 5 },
  17: { slots: 4, level: 5 }, 18: { slots: 4, level: 5 }, 19: { slots: 4, level: 5 }, 20: { slots: 4, level: 5 },
} as const;

export function autoSlotsForClassLevel(className: string | null | undefined, level: number): SpellSlotMap {
  const clamped = Math.max(1, Math.min(20, toInt(level, 1)));
  const progression = classProgression(className);
  if (progression === 'none') return {};
  if (progression === 'pact') {
    const pact = PACT[clamped];
    return pact ? { [`level_${pact.level}`]: { max: pact.slots, used: 0 } } : {};
  }
  const row = (progression === 'full' ? FULL : progression === 'half' ? HALF : THIRD)[clamped] || [];
  const slots: SpellSlotMap = {};
  row.forEach((count, index) => {
    if (count > 0) slots[`level_${index + 1}`] = { max: count, used: 0 };
  });
  return slots;
}
